import React from 'react';
import { CapabilityNoetivraNote } from './CapabilityNoetivraNote';
import { motion } from 'framer-motion';
import { Shield, Target, CheckCircle, XCircle } from 'lucide-react';
export function ManipulationResistancePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-875" data-mp="25efk0otktqx5">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-876" data-mp="2ab621hoxywl7">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-877" data-mp="2y1a4vmub2sb5">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-878" data-mp="2taa589tsxw4y">
            <span className="w-8 h-px bg-teal" data-id="element-879" data-mp="2t8qneeshzd12"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-880" data-mp="3gzcw88abt1c4">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-881" data-mp="3lsfhzu8v36hr"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-882" data-mp="2kb7b5t9u4lhr">
            Manipulation Resistance
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-883" data-mp="2t27qeu96bywl">
            At SyNeural, manipulation resistance refers to the ability to detect
            and resist propaganda, emotional framing, rhetorical deception,
            identity-based pressure, narrative capture, and other forms of
            influence designed to bypass clear reasoning.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-884" data-mp="3c0sesejokq6j">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-885" data-mp="380ry0n9402wb">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-886" data-mp="3crr1a3vi3y2m">
            It is not about distrusting everything. It is about being able to
            notice when someone is trying to move judgment through distortion,
            fear, tribal loyalty, status pressure, or selective framing rather
            than through sound reasoning and evidence.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-887" data-mp="3vq3d5xdsrm1w">
            This capability matters because modern individuals and institutions
            operate in environments saturated with persuasion. Information
            systems are increasingly optimized not for clarity, but for
            attention, reaction, compliance, and emotional intensity. Without
            manipulation resistance, people can become highly vulnerable to
            narratives that feel compelling while degrading judgment over time.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-888" data-mp="27rsssopfjb7v">
        <div className="max-w-4xl mx-auto" data-id="element-889" data-mp="23ppgjxuxs2lj">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-890" data-mp="23onw52s163r5">
            <div className="flex items-center gap-4 mb-6" data-id="element-891" data-mp="2mfx437m5kkgm">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-892" data-mp="2mijm616o6id3">
                <Target size={24} data-id="element-893" data-mp="2hndq49jjznba" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-894" data-mp="2cpm1ppi4ajbu">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-895" data-mp="3a7ufixloxah7">
              Manipulation resistance protects independent thought. It helps
              people preserve perspective, question emotional framing, and avoid
              being absorbed into narratives that distort reality or narrow
              judgment. Weak manipulation resistance can make individuals,
              communities, and institutions more vulnerable to misinformation,
              moral panic, ideological capture, group pressure, and poor
              decision-making under social stress.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Manipulation Resistance Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-896" data-mp="3doozwh37zx07">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-897" data-mp="2yxpsh9e4fei7">
          <div className="flex items-center gap-4 mb-6" data-id="element-898" data-mp="a2dovzufytj">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-899" data-mp="1kxcwco7m50gs">
              <CheckCircle size={24} data-id="element-900" data-mp="1fyieppldthy9" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-901" data-mp="1tqos53x5hau0">
              What Strong Manipulation Resistance Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-902" data-mp="1ppwii9kd93x7">
            People with stronger manipulation resistance tend to notice loaded
            language, emotional shortcuts, selective evidence, false urgency,
            simplistic enemies, and attempts to exploit identity, fear, or
            grievance. They are more likely to pause, verify, compare
            perspectives, and ask whether a message is informing them or merely
            trying to move them. This does not make them cynical. It makes them
            harder to capture.
          </p>
        </motion.div>
      </section>

      {/* What Weak Manipulation Resistance Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-903" data-mp="1f1tfm8y3iayk">
        <div className="max-w-4xl mx-auto" data-id="element-904" data-mp="1k16clmiokyev">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-905" data-mp="1k04qmn04mrid">
            <div className="flex items-center gap-4 mb-6" data-id="element-906" data-mp="10dgrfy591wem">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-907" data-mp="10bvxoaqrehht">
                <XCircle size={24} data-id="element-908" data-mp="15b8gmkeif4f4" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-909" data-mp="1a4jtb2606fey">
                What Weak Manipulation Resistance Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-910" data-mp="hmyz3uobx2ts">
              Weak manipulation resistance often shows up as susceptibility to
              rumor, strong reaction to emotional narratives, overreliance on
              group cues, and difficulty separating persuasion from truth.
              People weak in this area may accept claims because they are
              repeated often, socially rewarded, emotionally satisfying, or
              aligned with a preexisting identity. In civic and institutional
              environments, this can produce instability, poor judgment, and
              fractured trust.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-911" data-mp="3dky7ymegq5zz">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-912" data-mp="38mmwy8cflpg6">
          <div className="flex items-center gap-4 mb-6" data-id="element-913" data-mp="rdij4a2pxh9r">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-914" data-mp="wb1yxef2e23t">
              <Shield size={24} data-id="element-915" data-mp="ic2dsgqrg5ce" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-916" data-mp="idlrq0oy24b0">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-917" data-mp="8l7r5b9lr3qf">
            Manipulation resistance can be improved through media literacy,
            propaganda analysis, rhetoric study, logic training, long-form
            reading, debate, and historical awareness of mass persuasion. It
            also improves when people become more conscious of their own
            emotional triggers and identity vulnerabilities. In the SyNeural
            framework, this capability is essential for civic resilience,
            institutional stability, and trustworthy leadership.
          </p>
        </motion.div>
      </section>
      <CapabilityNoetivraNote capability="Manipulation resistance" data-id="element-918" data-mp="3762lm33x15wq" />
    </div>;
}